import { Fragment, ReactElement, useRef } from 'react';
import { Box } from '@zoopla-eng/zds-components/box';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { Button } from '@zoopla-eng/zds-components/button';

import { Question } from '../question';
import {
    StyledTitle,
    SuccessContainer,
    SuccessMessage
} from './your-situation.styles';
import { questionsMap } from './questions-map';
import { useQuestion, QuestionContext } from './useQuestion';
import { FocusableElement, QuestionNames } from '../question/question.types';
import { useUpdateUserProfileMutation } from '../queries/updateUserProfile.query.generated';
import { UserProfileInput, UserProfile } from '../../graphql.generated';
import {
    checkIsGoalValue,
    checkIsSituationValue,
    AnswerData
} from '../question/question.types';

export type YourSituationResultProps = {
    goal?: string | null;
    question: string | null;
};

export type YourSituationProps = {
    analyticsPage: string;
    actionAfterQuestionsElement: ReactElement;
    onSkip?: () => void;
    initialValues?: Omit<UserProfile, '__typename'>;
    shouldFocusFirstQuestionOnMount?: boolean;
    showEditButton?: boolean;
};

export const checkResponseType = (data: AnswerData) => {
    if (
        data['situationWhatIsYourCurrentPropertyGoal'] &&
        !checkIsGoalValue(data['situationWhatIsYourCurrentPropertyGoal'])
    ) {
        throw new Error('Invalid property goal value.');
    }

    const allQuestions = Object.keys(data).filter(
        (item) => item !== 'situationWhatIsYourCurrentPropertyGoal'
    );

    for (const val in allQuestions) {
        const key = allQuestions[val];
        if (!checkIsSituationValue(data[key as QuestionNames] || '')) {
            throw new Error('Invalid situation question value.');
        }
    }

    return {
        ...data
    } as UserProfileInput;
};

export const YourSituation = ({
    analyticsPage,
    actionAfterQuestionsElement,
    onSkip,
    initialValues,
    shouldFocusFirstQuestionOnMount = false,
    showEditButton = true
}: YourSituationProps) => {
    const [updateAnswer, { loading }] = useUpdateUserProfileMutation();

    const onSubmit = async (data: AnswerData) => {
        const input = checkResponseType(data);
        await updateAnswer({
            variables: {
                input
            }
        });
    };

    const latestQuestionRef = useRef<FocusableElement>(null);
    const successMessageRef = useRef<HTMLParagraphElement>(null);

    const useQuestionProps = {
        initialQuestionId: 'situationWhatIsYourCurrentPropertyGoal',
        initialValues,
        questionsMap,
        analyticsPage,
        onSkip,
        onSubmit,
        ref: {
            latestQuestionRef,
            successMessageRef
        },
        shouldFocusFirstQuestionOnMount
    };

    const {
        renderedSteps,
        lastQuestionId,
        isFirstQuestionFilled,
        onSelectedAnswer,
        onSubmitAnswer,
        onEditAnswer,
        onSkipButtonClicked,
        getInitialValue
    } = useQuestion(useQuestionProps);

    return (
        <>
            {isFirstQuestionFilled && (
                <Box pb="$S400">
                    <StyledTitle>My situation</StyledTitle>
                </Box>
            )}
            {renderedSteps.map((step) => {
                if (step.question === 'done') {
                    return (
                        <Fragment key="done">
                            <Box py="$M100">
                                <Divider direction="horizontal" />
                            </Box>
                            <SuccessContainer
                                ref={successMessageRef}
                                tabIndex={-1}
                            >
                                <Box pb="$S500">
                                    <SuccessMessage>
                                        Thanks for sharing with us.
                                        <br />
                                        We'll show more of what you need and
                                        less of what you don't.
                                    </SuccessMessage>
                                </Box>
                                <QuestionContext.Provider
                                    value={{
                                        answers: renderedSteps,
                                        lastQuestionId
                                    }}
                                >
                                    {actionAfterQuestionsElement}
                                </QuestionContext.Provider>
                            </SuccessContainer>
                        </Fragment>
                    );
                }

                const question = questionsMap[step.question as QuestionNames];
                const value = step.value
                    ? getInitialValue(step.value, question.options)
                    : undefined;

                return (
                    <Box key={step.question} pb="$S400">
                        <Question
                            label={question.label}
                            name={step.question}
                            options={question.options}
                            subtitle={question.subtitle}
                            action={{
                                onSubmit: onSubmitAnswer,
                                onSaved: onSelectedAnswer,
                                onEdit: onEditAnswer,
                                loading
                            }}
                            ref={latestQuestionRef}
                            formatFieldAnswer={question.formatAnswer}
                            initialValue={value}
                            analyticsPage={analyticsPage}
                            required
                            showEditButton={showEditButton}
                        />
                        {!isFirstQuestionFilled &&
                            typeof onSkip === 'function' && (
                                <Box>
                                    <Box pt="$M100" pb="$S400">
                                        <Divider direction="horizontal" />
                                    </Box>
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        fw
                                    >
                                        <Button
                                            data-testid="skip-button"
                                            category="tertiary"
                                            onClick={onSkipButtonClicked}
                                        >
                                            Skip for now
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                    </Box>
                );
            })}
        </>
    );
};
